import React, { useState, useMemo } from 'react';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const cadd    = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const cmul    = (a, b) => ({ re: a.re*b.re - a.im*b.im, im: a.re*b.im + a.im*b.re });
const cscale  = (c, s) => ({ re: c.re*s, im: c.im*s });
const cmag    = (c)    => Math.sqrt(c.re*c.re + c.im*c.im);
const cang    = (c)    => Math.atan2(c.im, c.re) * RAD;
const fromPolar = (mag, angDeg) => ({ re: mag*Math.cos(angDeg*DEG), im: mag*Math.sin(angDeg*DEG) });
const A_OP  = fromPolar(1, 120);
const A2_OP = fromPolar(1, 240);

function decompose(Va, Vb, Vc) {
  const V0 = cscale(cadd(Va, cadd(Vb, Vc)), 1/3);
  const V1 = cscale(cadd(Va, cadd(cmul(A_OP, Vb), cmul(A2_OP, Vc))), 1/3);
  const V2 = cscale(cadd(Va, cadd(cmul(A2_OP, Vb), cmul(A_OP, Vc))), 1/3);
  return { V0, V1, V2 };
}

// Six-phasor SVG diagram
const SixPhasorSVG = ({ Va, Vb, Vc, V0, V1, V2, dark }) => {
  const CX = 150, CY = 150, R = 105;
  const bg = dark ? '#0f172a' : '#ffffff';
  const ax = dark ? '#1e293b' : '#e2e8f0';
  const lc = dark ? '#475569' : '#94a3b8';

  const drawP = (vec, color, lbl, scale = R, dash = false) => {
    if (cmag(vec) < 0.02) return null;
    const mag  = Math.min(cmag(vec), 2.1);
    const rad  = Math.atan2(vec.im, vec.re);
    const sc   = scale / 2.0; // normalized so 1 pu fills half R
    const tx   = CX + mag*sc*Math.cos(rad);
    const ty   = CY - mag*sc*Math.sin(rad);
    const bx   = CX + (mag*sc - 10)*Math.cos(rad);
    const by   = CY - (mag*sc - 10)*Math.sin(rad);
    const hw   = 4.5;
    const pts  = `${tx},${ty} ${bx+hw*Math.sin(rad)},${by+hw*Math.cos(rad)} ${bx-hw*Math.sin(rad)},${by-hw*Math.cos(rad)}`;
    return (
      <g key={lbl}>
        <line x1={CX} y1={CY} x2={bx} y2={by} stroke={color} strokeWidth={dash ? '1.8' : '2.5'}
          strokeLinecap="round" strokeDasharray={dash ? '5 2' : undefined} />
        <polygon points={pts} fill={color} />
        <text x={tx + 14*Math.cos(rad)} y={ty - 14*Math.sin(rad) + 4}
          fontSize="10" fontWeight="700" fontFamily="IBM Plex Mono" fill={color} textAnchor="middle">
          {lbl}
        </text>
      </g>
    );
  };

  return (
    <svg viewBox="0 0 300 300" className="w-full max-w-[260px] mx-auto block">
      <rect width="300" height="300" fill={bg} rx="10" />
      <circle cx={CX} cy={CY} r={R/2} fill="none" stroke={ax} strokeWidth="0.8" strokeDasharray="3 4" />
      <circle cx={CX} cy={CY} r={R} fill="none" stroke={ax} strokeWidth="0.8" strokeDasharray="3 4" />
      <line x1={CX-R-8} y1={CY} x2={CX+R+8} y2={CY} stroke={ax} strokeWidth="0.8" />
      <line x1={CX} y1={CY-R-8} x2={CX} y2={CY+R+8} stroke={ax} strokeWidth="0.8" />
      <text x={CX+R+4} y={CY-3} fontSize="8" fill={lc} fontFamily="IBM Plex Mono">+Re</text>
      <text x={CX+3} y={CY-R-3} fontSize="8" fill={lc} fontFamily="IBM Plex Mono">+Im</text>
      {/* Phase phasors (solid) */}
      {drawP(Va, '#64748b', 'Va', R)}
      {drawP(Vb, '#6366f1', 'Vb', R)}
      {drawP(Vc, '#f59e0b', 'Vc', R)}
      {/* Sequence phasors (dashed) */}
      {drawP(V1, '#0d9488', 'V₁', R, true)}
      {drawP(V2, '#ea580c', 'V₂', R, true)}
      {drawP(V0, '#7c3aed', 'V₀', R, true)}
      <circle cx={CX} cy={CY} r="3" fill={dark ? '#475569' : '#94a3b8'} />
    </svg>
  );
};

const SequenceDecomposerCalc = () => {
  const { dark } = useTheme();

  const [inputs, setInputs] = useState({
    Va_mag: 1.0, Va_ang: 0,
    Vb_mag: 1.0, Vb_ang: -120,
    Vc_mag: 1.0, Vc_ang: 120,
  });

  const set = (key, val) => setInputs(prev => ({ ...prev, [key]: parseFloat(val) || 0 }));

  const { Va, Vb, Vc, V0, V1, V2 } = useMemo(() => {
    const Va = fromPolar(inputs.Va_mag, inputs.Va_ang);
    const Vb = fromPolar(inputs.Vb_mag, inputs.Vb_ang);
    const Vc = fromPolar(inputs.Vc_mag, inputs.Vc_ang);
    const { V0, V1, V2 } = decompose(Va, Vb, Vc);
    return { Va, Vb, Vc, V0, V1, V2 };
  }, [inputs]);

  const inputCls = 'w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 outline-none font-mono';

  const PHASES = [
    { key: 'Va', color: '#64748b', label: 'Va', mag_id: 'Va_mag', ang_id: 'Va_ang' },
    { key: 'Vb', color: '#6366f1', label: 'Vb', mag_id: 'Vb_mag', ang_id: 'Vb_ang' },
    { key: 'Vc', color: '#f59e0b', label: 'Vc', mag_id: 'Vc_mag', ang_id: 'Vc_ang' },
  ];

  return (
    <div data-testid="sequence-decomposer-calc" className="space-y-5">
      {/* Inputs */}
      <div className="space-y-3">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest">Phase Voltage Inputs</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse min-w-[380px]">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="py-2 px-2 text-left font-medium text-slate-500 w-12">Phase</th>
                <th className="py-2 px-2 text-left font-medium text-slate-500">Magnitude (pu)</th>
                <th className="py-2 px-2 text-left font-medium text-slate-500">Angle (°)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {PHASES.map(({ key, color, label, mag_id, ang_id }) => (
                <tr key={key}>
                  <td className="py-2 px-2">
                    <span className="font-mono font-bold text-sm" style={{ color }}>{label}</span>
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      <input type="number" value={inputs[mag_id]} step="0.01" min="0"
                        data-testid={`${mag_id}-input`}
                        onChange={e => set(mag_id, e.target.value)} className={inputCls} />
                      <TeachingTooltip showIcon
                        whatThis={`The RMS magnitude of the ${label} voltage phasor in per-unit. 1.0 pu = rated phase voltage.`}
                        tryThis={`Try ${label} magnitude = 0 to simulate a phase-to-ground fault. Watch how V₀ and V₁ respond.`}
                        whyMatters="Sequence magnitudes directly determine fault current levels. Equal V0=V1=V2 proves an SLG fault."
                        connectedTo="→ Updates V0, V1, V2 outputs and the phasor diagram." />
                    </div>
                  </td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1">
                      <input type="number" value={inputs[ang_id]} step="1" min="-180" max="180"
                        data-testid={`${ang_id}-input`}
                        onChange={e => set(ang_id, e.target.value)} className={inputCls} />
                      <span className="text-xs text-slate-500 font-mono">°</span>
                      <TeachingTooltip showIcon
                        whatThis={`The phase angle of ${label} measured counterclockwise from the positive real axis. Va = 0° is the standard reference.`}
                        tryThis={`Standard balanced set: Va=0°, Vb=-120°, Vc=+120°. Try Vb=-100° to introduce imbalance and observe V₂ grow.`}
                        whyMatters="The angular relationships between phases determine the sequence content. Equal spacing (120°) means zero negative/zero sequence."
                        connectedTo="→ Affects sequence component angles and magnitudes." />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Output cards */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'V₁ (Positive)', val: V1, color: '#0d9488', bg: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800', tid: 'v1-result' },
          { label: 'V₂ (Negative)', val: V2, color: '#ea580c', bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800', tid: 'v2-result' },
          { label: 'V₀ (Zero)',     val: V0, color: '#7c3aed', bg: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800', tid: 'v0-result' },
        ].map(({ label, val, color, bg, tid }) => (
          <div key={tid} data-testid={tid} className={`rounded-lg p-3 border text-center ${bg}`}>
            <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color }}>{label}</p>
            <p className="text-base font-mono font-bold" style={{ color }}>{cmag(val).toFixed(4)}</p>
            <p className="text-[10px] font-mono mt-0.5" style={{ color }}>pu ∠{cang(val).toFixed(2)}°</p>
            <p className="text-[9px] text-slate-500 dark:text-slate-500 mt-1 font-mono">
              {val.re.toFixed(4)} {val.im >= 0 ? '+' : '−'} j{Math.abs(val.im).toFixed(4)}
            </p>
          </div>
        ))}
      </div>

      {/* Six-phasor diagram */}
      <div>
        <p className="text-xs font-medium text-slate-500 dark:text-slate-500 mb-2">All six phasors — phase (solid) and sequence (dashed)</p>
        <SixPhasorSVG Va={Va} Vb={Vb} Vc={Vc} V0={V0} V1={V1} V2={V2} dark={dark} />
        <p className="text-[10px] text-center text-slate-400 dark:text-slate-600 mt-1">
          Solid = phase phasors (Va/Vb/Vc) · Dashed = sequence components (V₁/V₂/V₀)
        </p>
      </div>

      {/* Sum check */}
      <div className="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3">
        <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Verification: |V₁|² + |V₂|² + |V₀|² vs total</p>
        <p className="text-xs font-mono text-slate-600 dark:text-slate-400">
          |V₁| = {cmag(V1).toFixed(4)} · |V₂| = {cmag(V2).toFixed(4)} · |V₀| = {cmag(V0).toFixed(4)}
        </p>
        <p className="text-xs font-mono text-teal-700 dark:text-teal-400 mt-1">
          Va = V₀+V₁+V₂ = {(V0.re + V1.re + V2.re).toFixed(4)} + j{(V0.im + V1.im + V2.im).toFixed(4)} 
          {' '}≈ {cmag(fromPolar(inputs.Va_mag, inputs.Va_ang)).toFixed(4)} pu ✓
        </p>
      </div>
    </div>
  );
};

export default SequenceDecomposerCalc;
